const _excluded = ["id", "itemToString", "items", "groupedItems", "target", "overlayRef", "layoutDependency", "getMenuProps", "stateReducer"],
  _excluded2 = ["isOpen", "getItemProps", "selectItem", "getMenuProps", "getToggleButtonProps", "openMenu", "highlightedIndex", "selectedItem"],
  _excluded3 = ["onKeyDown"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useSelect } from 'downshift';
import { mergeRefs, useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';
import useDropdownId from './useDropdownId';
import useFooterEvents from './useFooterEvents';
import useGroupedItems from './useGroupedItems';
import useVirtualProps from './useVirtualProps';
import { checkReferenceEquality, defaultLabelGetter, getAriaProps, isGroupHeader, wrapGetItemProps } from '../helpers';
import { getSingleSelectionHandlers } from '../helpers/sharedSingleSelection';
/**
 * A hook used to manage all logic needed for a Dropdown.
 */
const useDropdown = _ref => {
  var _targetRef$current;
  let {
      id,
      itemToString = item => defaultLabelGetter(item),
      items,
      groupedItems,
      target,
      overlayRef: overlayRefProp,
      layoutDependency,
      getMenuProps: getMenuPropsProp,
      stateReducer: stateReducerProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const dropdownId = useDropdownId(id);
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality(_objectSpread(_objectSpread({}, props), {}, {
    items: maybeGroupedItems
  }));
  const ariaProps = getAriaProps(props);
  const targetRef = useForwardedRef(target || null);
  const overlayRef = useForwardedRef(overlayRefProp || null);
  const {
    flatItems,
    indexGetter,
    getMenuItems
  } = useGroupedItems({
    maybeGroupedItems
  });
  const {
    onKeyDown: controlOnKeyDown,
    getShouldKeepMenuOpenOnBlur
  } = useFooterEvents({
    id: dropdownId
  });
  const dropdownStateReducer = useCallback((state, _ref2) => {
    let {
      changes,
      type
    } = _ref2;
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonBlur:
        return _objectSpread(_objectSpread({}, changes), {}, {
          isOpen: getShouldKeepMenuOpenOnBlur()
        });
      default:
        return changes;
    }
  }, [getShouldKeepMenuOpenOnBlur]);
  const stateReducer = useCallback((state, actionAndChanges) => {
    const updatedState = dropdownStateReducer(state, actionAndChanges);
    const customUpdatedState = stateReducerProp === null || stateReducerProp === void 0 ? void 0 : stateReducerProp(state, actionAndChanges);
    return _objectSpread(_objectSpread(_objectSpread({}, actionAndChanges.changes), updatedState), customUpdatedState);
  }, [dropdownStateReducer, stateReducerProp]);
  const _useSelect = useSelect(_objectSpread({
      id: dropdownId,
      itemToString,
      items: flatItems,
      stateReducer
    }, props)),
    {
      isOpen: downshiftIsMenuOpen,
      getItemProps: rawGetItemProps,
      selectItem,
      getMenuProps: rawGetMenuProps,
      getToggleButtonProps: rawGetToggleButtonProps,
      openMenu,
      highlightedIndex,
      selectedItem
    } = _useSelect,
    useSelectReturnValue = _objectWithoutProperties(_useSelect, _excluded2);
  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps
  } = useOverlay(_objectSpread({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    target: targetRef,
    overlayRef,
    layoutDependency,
    isOverlayConditionallyRendered: false
  }, props));
  const getMenuProps = useCallback((options, otherOptions) => {
    const temp = getMenuPropsProp ? rawGetMenuProps(getMenuPropsProp(_objectSpread(_objectSpread({}, overlayProps), options), otherOptions), otherOptions) : rawGetMenuProps(_objectSpread(_objectSpread({}, overlayProps), options), otherOptions);
    return temp;
  }, [getMenuPropsProp, overlayProps, rawGetMenuProps]);

  // TODO: deduplicate this

  const menuItems = useMemo(() => getMenuItems(flatItems), [flatItems, getMenuItems]);
  const defaultIndexGetter = useCallback(item => menuItems.indexOf(item), [menuItems]);
  const getItemProps = useCallback(args => wrapGetItemProps({
    getItemProps: rawGetItemProps,
    highlightedIndex,
    selectedItem,
    indexGetter: indexGetter || defaultIndexGetter
  })(args), [defaultIndexGetter, highlightedIndex, indexGetter, rawGetItemProps, selectedItem]);
  const getToggleButtonProps = useCallback(function () {
    let _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let {
        onKeyDown: onKeyDownProp
      } = _ref3,
      options = _objectWithoutProperties(_ref3, _excluded3);
    const onKeyDown = e => {
      controlOnKeyDown(e);
      onKeyDownProp === null || onKeyDownProp === void 0 ? void 0 : onKeyDownProp(e);
    };
    return rawGetToggleButtonProps(_objectSpread(_objectSpread({}, options), {}, {
      onKeyDown
    }));
  }, [controlOnKeyDown, rawGetToggleButtonProps]);
  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);
  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader
  });
  const singleSelectionHandlers = getSingleSelectionHandlers({
    id: dropdownId,
    selectItem,
    closeMenu: useSelectReturnValue.closeMenu,
    refToFocusOnClear: targetRef,
    selectedItem
  });
  return _objectSpread({
    controlProps: _objectSpread({
      ref: targetRef,
      isOpen: useOverlayIsOpen,
      isAutocomplete: false,
      getToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem
    }, ariaProps),
    menuProps: _objectSpread({
      isOpen: useOverlayIsOpen,
      isAutocomplete: false,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex,
      selectedItem,
      width: (_targetRef$current = targetRef.current) === null || _targetRef$current === void 0 ? void 0 : _targetRef$current.offsetWidth,
      selectItem,
      isGroupHeader
    }, singleSelectionHandlers.menuProps),
    /**
     * Props to be provided to `@tanstack/react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem
  }, useSelectReturnValue);
};
export default Object.assign(useDropdown, {
  stateChangeTypes: useSelect.stateChangeTypes
});
//# sourceMappingURL=useDropdown.js.map